import React, { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { AlertCircle, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CreateAccountModal = ({ open, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState(1);
  const [accountType, setAccountType] = useState('');
  
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    middle_name: '',
    last_name: '',
    mobile_number: '',
    role: '',
    // Provider specific
    provider_role: '',
    clinic_hours: '',
    phone: '',
    // Employer specific
    company_name: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleRoleSelect = (role) => {
    setAccountType(role);
    setFormData({ ...formData, role });
    setStep(2);
  };

  const validatePhoneNumber = (phone) => {
    // Philippine mobile number format: +639XXXXXXXXX or 09XXXXXXXXX
    const pattern = /^(\+639|09)\d{9}$/;
    return pattern.test(phone);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate mobile number
    if (!validatePhoneNumber(formData.mobile_number)) {
      setError('Invalid mobile number format. Please use +639XXXXXXXXX or 09XXXXXXXXX format.');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/admin/create-account`,
        formData,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      
      toast.success(
        `Account created successfully!\nEmail: ${formData.email}\nTemporary Password: ${response.data.temporary_password}`,
        { duration: 10000 }
      );
      
      onSuccess();
      resetForm();
    } catch (err) {
      setError(err.response?.data?.detail || 'An error occurred while creating account');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      first_name: '',
      middle_name: '',
      last_name: '',
      mobile_number: '',
      role: '',
      provider_role: '',
      clinic_hours: '',
      phone: '',
      company_name: ''
    });
    setStep(1);
    setAccountType('');
    setError('');
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={resetForm}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create New Staff Account</DialogTitle>
        </DialogHeader>

        <Alert className="mb-4 bg-blue-50 border-blue-200">
          <AlertCircle className="h-4 w-4 text-blue-700" />
          <AlertDescription className="text-blue-900">
            This creates <strong>Provider</strong>, <strong>Admin</strong>, or <strong>Employer</strong> accounts. 
            To add patients, use the "Add Patient" button on the Patient Census Dashboard.
          </AlertDescription>
        </Alert>

        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {step === 1 && (
          <div className="space-y-4">
            <p className="text-slate-600 mb-4">Select the type of staff account to create:</p>
            
            <button
              onClick={() => handleRoleSelect('provider')}
              className="w-full p-6 border-2 border-slate-200 rounded-lg hover:border-cyan-500 hover:bg-cyan-50 transition-all text-left"
            >
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Provider Account</h3>
              <p className="text-sm text-slate-600">
                Create an account for a healthcare provider (doctor, nurse, etc.) who will have access to view and edit patient clinical data.
              </p>
            </button>

            <button
              onClick={() => handleRoleSelect('admin')}
              className="w-full p-6 border-2 border-slate-200 rounded-lg hover:border-cyan-500 hover:bg-cyan-50 transition-all text-left"
              data-testid="role-select-admin"
            >
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Admin/Staff Account</h3>
              <p className="text-sm text-slate-600">
                Create an account for administrative staff who will manage patient information, memberships, and accounts (no clinical data access).
              </p>
            </button>

            <button
              onClick={() => handleRoleSelect('employer')}
              className="w-full p-6 border-2 border-slate-200 rounded-lg hover:border-cyan-500 hover:bg-cyan-50 transition-all text-left"
              data-testid="role-select-employer"
            >
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Employer Account</h3>
              <p className="text-sm text-slate-600">
                Create an account for an employer or company that sponsors employee health memberships. They can view their employees' enrollment status and interview completion.
              </p>
            </button>

            <div className="flex justify-end pt-4">
              <Button onClick={resetForm} className="btn-secondary">
                Cancel
              </Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="p-4 bg-cyan-50 rounded-lg border border-cyan-200">
              <p className="text-sm text-cyan-900">
                <strong>Creating: {accountType === 'provider' ? 'Provider' : accountType === 'employer' ? 'Employer' : 'Admin/Staff'} Account</strong>
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="first_name">First Name *</Label>
                <Input
                  id="first_name"
                  name="first_name"
                  type="text"
                  value={formData.first_name}
                  onChange={handleChange}
                  required
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="middle_name">Middle Name *</Label>
                <Input
                  id="middle_name"
                  name="middle_name"
                  type="text"
                  value={formData.middle_name}
                  onChange={handleChange}
                  required
                  className="mt-1"
                  placeholder="All Filipinos have middle names"
                />
              </div>

              <div className="col-span-2">
                <Label htmlFor="last_name">Last Name *</Label>
                <Input
                  id="last_name"
                  name="last_name"
                  type="text"
                  value={formData.last_name}
                  onChange={handleChange}
                  required
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="mt-1"
                  placeholder="email@example.com"
                />
              </div>

              <div>
                <Label htmlFor="mobile_number">Mobile Number *</Label>
                <Input
                  id="mobile_number"
                  name="mobile_number"
                  type="tel"
                  value={formData.mobile_number}
                  onChange={handleChange}
                  required
                  pattern="(\+639|09)\d{9}"
                  title="Philippine mobile number: +639XXXXXXXXX or 09XXXXXXXXX"
                  className="mt-1"
                  placeholder="+639171234567 or 09171234567"
                />
                <p className="text-xs text-slate-500 mt-1">Format: +639XXXXXXXXX or 09XXXXXXXXX</p>
              </div>

              <div className="col-span-2">
                <p className="text-xs text-slate-500">A temporary password will be generated and shown after creation</p>
              </div>

              {accountType === 'provider' && (
                <>
                  <div className="col-span-2">
                    <Label htmlFor="provider_role">Provider Role/Title *</Label>
                    <Input
                      id="provider_role"
                      name="provider_role"
                      type="text"
                      value={formData.provider_role}
                      onChange={handleChange}
                      required
                      className="mt-1"
                      placeholder="e.g., Primary Care Physician, Registered Nurse"
                    />
                  </div>

                  <div>
                    <Label htmlFor="clinic_hours">Clinic Hours *</Label>
                    <Input
                      id="clinic_hours"
                      name="clinic_hours"
                      type="text"
                      value={formData.clinic_hours}
                      onChange={handleChange}
                      required
                      className="mt-1"
                      placeholder="e.g., Mon-Fri, 9AM-5PM"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="mt-1"
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                </>
              )}

              {accountType === 'employer' && (
                <div className="col-span-2">
                  <Label htmlFor="company_name">Company / Business Name *</Label>
                  <Input
                    id="company_name"
                    name="company_name"
                    type="text"
                    value={formData.company_name}
                    onChange={handleChange}
                    required
                    className="mt-1"
                    placeholder="e.g., Acme Corporation"
                    data-testid="employer-company-name-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">This name will be displayed on the employer's dashboard</p>
                </div>
              )}
            </div>

            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-900">
                <strong>Note:</strong> The user will receive their login credentials via email (mocked for now). 
                They should change their password on first login.
              </p>
            </div>

            <div className="flex gap-4 pt-4">
              <Button
                type="button"
                onClick={() => setStep(1)}
                className="flex-1 btn-secondary"
              >
                Back
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="flex-1 btn-primary"
              >
                {loading ? 'Creating Account...' : 'Create Account'}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CreateAccountModal;